import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Award, Printer } from "lucide-react";
import { recordCertificate } from "../lib/api";
import { getClientId } from "../lib/storage";

export default function CertificateModal({ open, onClose, sectionId, sectionTitle, items, acks }) {
    const [name, setName] = useState("");
    const completedItems = items.filter(h => acks[h.slug]);
    const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

    if (!open) return null;

    const handlePrint = () => {
        // Fire-and-forget server log
        recordCertificate({
            client_id: getClientId(),
            section_id: sectionId,
            section_title: sectionTitle,
            volunteer_name: name.trim(),
            slugs: completedItems.map(i => i.slug),
        });
        window.print();
    };

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 print:p-0 print:bg-white" data-testid="certificate-modal">
            <div className="absolute inset-0 bg-black/40 print:hidden" onClick={onClose} />

            <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto print:shadow-none print:max-h-none print:max-w-none">
                <header className="flex items-center justify-between px-6 h-14 border-b border-gray-200 print:hidden">
                    <div className="font-semibold text-gray-900" style={{ fontFamily: "Outfit" }}>Completion Certificate</div>
                    <button onClick={onClose} className="p-2 -mr-2 rounded-md hover:bg-gray-100" aria-label="Close" data-testid="close-certificate">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                {/* Editable name (hidden in print) */}
                <div className="px-6 sm:px-8 pt-6 print:hidden">
                    <label className="block text-sm font-medium text-gray-900 mb-2" htmlFor="cert-name">Volunteer name</label>
                    <input
                        id="cert-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your full name"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-lg focus:ring-2 focus:ring-[#2C7A85] focus:border-transparent outline-none"
                        data-testid="cert-name-input"
                    />
                </div>

                {/* Certificate body (printable) */}
                <div className="certificate p-8 sm:p-12 print:p-12" data-testid="certificate-body">
                    <div className="border-4 border-[#2C7A85] rounded-lg p-8 sm:p-12 relative">
                        <div className="absolute top-4 right-4 print:top-6 print:right-6">
                            <img src="/bscc-logo.png" alt="BSCC" className="h-16 sm:h-20 w-auto" />
                        </div>

                        <div className="flex items-center gap-2 mb-6">
                            <Award className="h-6 w-6 text-[#E57373]" />
                            <div className="text-[11px] uppercase tracking-[0.25em] text-[#E57373] font-bold">Certificate of Completion</div>
                        </div>

                        <div className="mb-6">
                            <div className="text-sm text-gray-500 mb-2">This is to certify that</div>
                            <div className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight" style={{ fontFamily: "Outfit" }}>
                                {name || "________________________"}
                            </div>
                        </div>

                        <div className="mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                            has reviewed and acknowledged the following guidance from the BSCC Driver Handbook section <strong>"{sectionTitle}"</strong>:
                        </div>

                        <ul className="mb-8 space-y-1.5 text-[15px] text-gray-800">
                            {completedItems.length === 0 ? (
                                <li className="italic text-gray-500">No items marked complete yet.</li>
                            ) : (
                                completedItems.map(h => (
                                    <li key={h.slug} className="flex items-baseline gap-2">
                                        <span className="text-[#2C7A85]">✓</span>
                                        <span>{h.text}</span>
                                    </li>
                                ))
                            )}
                        </ul>

                        <div className="flex flex-wrap justify-between items-end gap-6 pt-6 border-t border-gray-200">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Date</div>
                                <div className="text-base text-gray-900 font-medium">{today}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Issued by</div>
                                <div className="text-base text-gray-900 font-medium">Badenoch &amp; Strathspey Community Connections</div>
                                <div className="text-xs text-gray-500">Scottish Charity SC029556</div>
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="px-6 sm:px-8 pb-6 flex flex-wrap gap-3 justify-end print:hidden">
                    <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-testid="cert-cancel">
                        Close
                    </button>
                    <button
                        onClick={handlePrint}
                        disabled={!name.trim() || completedItems.length === 0}
                        className="inline-flex items-center gap-2 bg-[#2C7A85] hover:bg-[#1F5F69] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
                        data-testid="cert-print"
                    >
                        <Printer className="h-4 w-4" /> Print / Save as PDF
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    );
}
